import { useEffect, useMemo, useState } from 'react';
import { Pill } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { can } from '../../lib/permissions';
import { useAuth } from '../../lib/auth';
import { BOM_QC_REVIEW_LABEL } from '../../lib/types';
import type { BomOperation, QcCheckpoint, BomQcReviewStatus } from '../../lib/types';

// 决策二：工序「是否需检」是【质量的三态审核】，不是建 BOM 时顺手勾的布尔。
//
// 为什么必须是三态而不是布尔：
//   布尔只有「要检 / 不要检」两个值，于是把「还没人判过」和「已确认不用检」压成同一个 0。
//   迁移前 347 道工序全是 0，看上去像「都确认免检」，实际是「一道都没审」——
//   布尔无法表达这个区别，于是「未审核」被静默当成「免检」放行了。
//   三态把「未判定」显式留在系统里：pending 才拦得住批准，not_required 才必须写理由。
//
// 判定权归质量：required 必须绑检查点（没检测依据，标了也执行不了），
// not_required 必须写理由（免检同样是质量决定，要能追溯）。

function statusOf(op: BomOperation): BomQcReviewStatus {
  if (op.qc_review_status) return op.qc_review_status;
  // 兼容迁移前/未重算的历史行：qc_required 是派生投影，反推只能给出最接近的读数
  return op.qc_required ? 'required' : 'not_required';
}

export function QcReviewPill({ op }: { op: BomOperation }) {
  const status = statusOf(op);
  if (status === 'required') {
    return op.qc_checkpoint_id
      ? <Pill tone="warn" label={BOM_QC_REVIEW_LABEL.required} />
      : <Pill tone="bad" label="必检·未绑检查点" />;
  }
  if (status === 'pending') return <Pill tone="neutral" label={BOM_QC_REVIEW_LABEL.pending} />;
  return <Pill tone="ok" label={BOM_QC_REVIEW_LABEL.not_required} />;
}

/** 审核依据：只读方要能看清「为什么这道工序免检」或「检什么」。 */
export function QcReviewNote({ op }: { op: BomOperation }) {
  const status = statusOf(op);
  if (status === 'required' && op.qc_checkpoint_id) {
    return <span className="muted">检查点 {op.qc_checkpoint_id.slice(0, 8)}</span>;
  }
  if (status === 'not_required' && op.qc_review_reason) {
    return <span className="muted">{op.qc_review_reason}</span>;
  }
  if (status === 'pending') return <span className="muted">尚未判定</span>;
  return <span className="muted">—</span>;
}

interface ReviewDraft { status: BomQcReviewStatus; checkpointId: string; reason: string; }

function draftOf(op: BomOperation): ReviewDraft {
  return {
    status: statusOf(op),
    checkpointId: op.qc_checkpoint_id ?? '',
    reason: op.qc_review_reason ?? '',
  };
}

/**
 * 单道工序的审核编辑器。只有质量（或管理员）能提交，其他角色只读。
 * 前端校验只为少一次往返；真正的强制在后端 qc-review 权限与 BOM 审批门禁。
 */
export function QcReviewEditor({ bomId, op, onSaved }: { bomId: string; op: BomOperation; onSaved: () => void }) {
  const { user } = useAuth();
  const mayReview = can(user, 'qc-review', 'create') || can(user, 'qc-review', 'edit');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReviewDraft>(() => draftOf(op));

  // 工序被他人改动（例如另一位质量同事先审了）时，把编辑器重置回最新事实
  useEffect(() => { setDraft(draftOf(op)); }, [op.qc_review_status, op.qc_checkpoint_id, op.qc_review_reason]);

  if (!mayReview) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
        <QcReviewPill op={op} />
        <QcReviewNote op={op} />
      </div>
    );
  }

  if (!open) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
        <QcReviewPill op={op} />
        <QcReviewNote op={op} />
        <button className="btn ghost" onClick={() => { setErr(null); setOpen(true); }}>审核</button>
      </div>
    );
  }

  // required 才要选检查点；not_required 只需要理由；pending 两者都不要求
  const needsCheckpoint = draft.status === 'required';
  const needsReason = draft.status === 'not_required';
  const invalid = (needsCheckpoint && !draft.checkpointId) || (needsReason && !draft.reason.trim());

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api.put(`/api/boms/${bomId}/operations/${op.id}/qc-review`, {
        qc_review_status: draft.status,
        qc_checkpoint_id: needsCheckpoint ? draft.checkpointId : null,
        qc_review_reason: draft.reason.trim() || null,
      });
      setOpen(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 240 }}>
      <select
        aria-label="质检审核状态"
        value={draft.status}
        onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as BomQcReviewStatus }))}
      >
        <option value="pending">待审核</option>
        <option value="required">必须检验（需绑检查点）</option>
        <option value="not_required">已确认免检（需填理由）</option>
      </select>
      {needsCheckpoint
        ? <CheckpointPicker value={draft.checkpointId} onChange={(v) => setDraft((d) => ({ ...d, checkpointId: v }))} />
        : null}
      {needsCheckpoint ? <span className="muted">选择这道工序实际要检查的内容，例如尺寸或硬度；请按工艺标准确认，不能仅为通过审批随意选择。纯机加工不代表免检。不确定时保留“待审核”。</span> : null}
      {needsReason ? (
        <input
          placeholder="免检理由（如：包装工序不涉尺寸）"
          value={draft.reason}
          onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
        />
      ) : null}
      {err ? <span style={{ color: 'var(--bad)' }}>{err}</span> : null}
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="btn" disabled={busy || invalid} onClick={save}>保存</button>
        <button className="btn ghost" disabled={busy} onClick={() => setOpen(false)}>取消</button>
      </div>
    </div>
  );
}

/**
 * 检查点选择器：列出质检模板里的真实检查点。
 * 刻意不按工序码硬编码映射——同一道工序在不同产品/BOM 上可能对应不同检测内容，
 * 必须让质量逐条挑（T44 第 5 步）。
 */
function CheckpointPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [options, setOptions] = useState<QcCheckpoint[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const templates = await api.list<{ code: string; name_zh?: string }>('/api/qc-templates', { limit: 100 });
        const codes = templates.map((t) => t.code).filter(Boolean);
        const displayLabels: Record<string, string> = {};
        const all: QcCheckpoint[] = [];
        for (const code of codes) {
          const d = await api.detail<{ stages?: { stage_name?: string; checkpoints?: QcCheckpoint[] }[] }>(
            `/api/qc-templates/${encodeURIComponent(code)}`,
          );
          for (const st of d.stages ?? []) for (const c of st.checkpoints ?? []) {
            all.push(c);
            displayLabels[c.id] = [templates.find(t => t.code === code)?.name_zh || code, st.stage_name, c.name_zh].filter(Boolean).join(' / ');
          }
        }
        if (alive) { setOptions(all); setLabels(displayLabels); setLoadErr(null); }
      } catch (e) {
        if (alive) setLoadErr(e instanceof ApiError ? e.message : '检查点加载失败');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => {
    const byKind = new Map<string, QcCheckpoint[]>();
    for (const c of options) {
      const k = c.inspection_kind ?? 'other';
      const arr = byKind.get(k) ?? [];
      arr.push(c);
      byKind.set(k, arr);
    }
    return [...byKind.entries()];
  }, [options]);

  if (loading) return <span className="muted">加载检查点…</span>;
  if (loadErr) return <span style={{ color: 'var(--bad)' }}>{loadErr}</span>;
  if (!options.length) return <span className="muted">无已导入的质检检查点</span>;

  return (
    <select aria-label="质检检查点" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— 选择检查点 —</option>
      {groups.map(([kind, list]) => (
        <optgroup key={kind} label={kind}>
          {list.map((c) => <option key={c.id} value={c.id}>{labels[c.id] || c.name_zh}</option>)}
        </optgroup>
      ))}
    </select>
  );
}
